import { useEffect, useState, useCallback, useRef } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { AppLayout } from '@/components/feature/AppLayout';
import { useAuthContext } from '@/contexts/AuthContext';
import { supabase } from '@/lib/supabase';
import { logAudit } from '@/lib/audit';
import TaskModal, { TaskFormData } from '../components/TaskModal';
import SprintModal, { SprintFormData } from '../components/SprintModal';
import TaskViewModal from '@/pages/app/projeto/[id]/components/TaskViewModal';

interface Task {
  id: string;
  project_id: string;
  title: string;
  description: string | null;
  type: 'feature' | 'bug';
  priority: string;
  board_status: string;
  requester_name: string | null;
  observations: string | null;
  estimate_hours: number;
  estimate_days: number;
  value: number;
  paid: boolean;
  sprint_desired: boolean;
  sprint_id: string | null;
  position: number;
  created_by: string | null;
  created_at: string;
}

interface Sprint {
  id: string;
  name: string;
  goal: string | null;
  status: string;
  start_date: string | null;
  end_date: string | null;
}

interface Project {
  id: string;
  name: string;
  _client_name?: string;
}

const COLUMNS: { key: string; label: string; color: string; bg: string; dot: string }[] = [
  { key: 'backlog', label: 'Backlog', color: 'text-slate-600', bg: 'bg-slate-100', dot: 'bg-slate-400' },
  { key: 'todo', label: 'A Fazer', color: 'text-sky-700', bg: 'bg-sky-50', dot: 'bg-sky-400' },
  { key: 'doing', label: 'Fazendo', color: 'text-amber-700', bg: 'bg-amber-50', dot: 'bg-amber-400' },
  { key: 'validation', label: 'Em Validação', color: 'text-violet-700', bg: 'bg-violet-50', dot: 'bg-violet-400' },
  { key: 'done', label: 'Concluído', color: 'text-emerald-700', bg: 'bg-emerald-50', dot: 'bg-emerald-500' },
];

const PRIORITY_CONFIG: Record<string, { label: string; color: string; icon: string }> = {
  low: { label: 'Baixa', color: 'text-slate-400', icon: 'ri-arrow-down-line' },
  medium: { label: 'Média', color: 'text-orange-400', icon: 'ri-arrow-right-line' },
  high: { label: 'Alta', color: 'text-red-500', icon: 'ri-arrow-up-line' },
  critical: { label: 'Crítica', color: 'text-red-700', icon: 'ri-arrow-up-double-line' },
};

const SPRINT_STATUS_CONFIG: Record<string, { label: string; color: string }> = {
  future: { label: 'Sprint Futura', color: 'bg-slate-100 text-slate-600' },
  active: { label: 'Sprint Atual', color: 'bg-emerald-100 text-emerald-700' },
  closed: { label: 'Encerrada', color: 'bg-slate-200 text-slate-500' },
};

export default function BoardPage() {
  const { projectId } = useParams<{ projectId: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const { profile } = useAuthContext();
  const isAdmin = profile?.role === 'admin';

  const [project, setProject] = useState<Project | null>(null);
  const [tasks, setTasks] = useState<Task[]>([]);
  const [sprints, setSprints] = useState<Sprint[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedSprint, setSelectedSprint] = useState<string>('all');
  const [taskModal, setTaskModal] = useState<{ open: boolean; task?: Task; defaultStatus?: string }>({ open: false });
  const [sprintModal, setSprintModal] = useState<{ open: boolean; sprint?: Sprint }>({ open: false });
  const [dragOverCol, setDragOverCol] = useState<string | null>(null);
  const [draggingId, setDraggingId] = useState<string | null>(null);
  const [viewTask, setViewTask] = useState<Task | null>(null);

  const locationState = location.state as { openTaskId?: string } | null;
  const [pendingOpenTaskId] = useState<string | null>(locationState?.openTaskId || null);

  // Ref para guardar o taskId sendo arrastado (mais confiável que dataTransfer em alguns browsers)
  const dragTaskIdRef = useRef<string | null>(null);

  const loadData = useCallback(async () => {
    if (!projectId) return;
    setLoading(true);

    const [projRes, tasksRes, sprintsRes] = await Promise.all([
      supabase.from('projects').select('id, name, client_id').eq('id', projectId).maybeSingle(),
      supabase.from('tasks').select('*').eq('project_id', projectId).order('position').order('created_at', { ascending: false }),
      supabase.from('sprints').select('*').eq('project_id', projectId).order('created_at'),
    ]);

    if (projRes.data) {
      const { data: client } = projRes.data.client_id
        ? await supabase.from('clients').select('name').eq('id', projRes.data.client_id).maybeSingle()
        : { data: null };
      setProject({ ...projRes.data, _client_name: client?.name || '—' });
    }

    setTasks(tasksRes.data || []);
    setSprints(sprintsRes.data || []);
    setLoading(false);
  }, [projectId]);

  useEffect(() => { loadData(); }, [loadData]);

  // Auto-open TaskViewModal when coming from a notification
  useEffect(() => {
    if (!pendingOpenTaskId || tasks.length === 0) return;
    const target = tasks.find(t => t.id === pendingOpenTaskId);
    if (target) {
      setViewTask(target);
    }
  }, [pendingOpenTaskId, tasks]);

  const getColumnTasks = (colKey: string) =>
    tasks.filter(t => {
      if (t.board_status !== colKey) return false;
      if (selectedSprint === 'all') return true;
      if (selectedSprint === 'none') return !t.sprint_id;
      return t.sprint_id === selectedSprint;
    });

  // Atualiza estado local imediatamente e persiste no banco
  const handleMoveTask = useCallback(async (taskId: string, newStatus: string) => {
    // Atualização otimista imediata no estado local
    setTasks(prev =>
      prev.map(t =>
        t.id === taskId ? { ...t, board_status: newStatus } : t
      )
    );

    const { error } = await supabase
      .from('tasks')
      .update({
        board_status: newStatus,
        updated_at: new Date().toISOString(),
      })
      .eq('id', taskId);

    if (error) {
      console.error('Erro ao mover task:', error);
      // Reverter em caso de erro
      loadData();
    } else {
      await logAudit('update', 'tasks', taskId, { board_status: newStatus });
    }
  }, [loadData]);

  // Drag handlers
  const handleDragStart = useCallback((e: React.DragEvent, taskId: string) => {
    dragTaskIdRef.current = taskId;
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', taskId);
    setDraggingId(taskId);
  }, []);

  const handleDragEnd = useCallback(() => {
    dragTaskIdRef.current = null;
    setDraggingId(null);
    setDragOverCol(null);
  }, []);

  const handleDragOver = useCallback((e: React.DragEvent, colKey: string) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    setDragOverCol(colKey);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    // Só limpa se saiu do container da coluna (não de um filho)
    if (!e.currentTarget.contains(e.relatedTarget as Node)) {
      setDragOverCol(null);
    }
  }, []);

  const handleDrop = useCallback((e: React.DragEvent, colKey: string) => {
    e.preventDefault();
    e.stopPropagation();

    // Tenta pegar o taskId pelo ref primeiro (mais confiável), depois pelo dataTransfer
    const taskId = dragTaskIdRef.current || e.dataTransfer.getData('text/plain');

    setDragOverCol(null);
    setDraggingId(null);
    dragTaskIdRef.current = null;

    if (!taskId || !isAdmin) return;

    // Verifica se o card está sendo movido para uma coluna diferente
    const task = tasks.find(t => t.id === taskId);
    if (!task || task.board_status === colKey) return;

    handleMoveTask(taskId, colKey);
  }, [isAdmin, tasks, handleMoveTask]);

  const handleAdvance = useCallback((task: Task) => {
    const currentIdx = COLUMNS.findIndex(c => c.key === task.board_status);
    if (currentIdx < 0 || currentIdx >= COLUMNS.length - 1) return;
    const nextCol = COLUMNS[currentIdx + 1];
    handleMoveTask(task.id, nextCol.key);
  }, [handleMoveTask]);

  const handleSaveTask = async (data: TaskFormData) => {
    if (!projectId) return;
    if (taskModal.task) {
      const { error } = await supabase.from('tasks').update({
        ...data,
        updated_at: new Date().toISOString(),
      }).eq('id', taskModal.task.id);
      if (!error) {
        await logAudit('update', 'tasks', taskModal.task.id, { title: data.title });
        loadData();
      }
    } else {
      const colTasks = tasks.filter(t => t.board_status === (taskModal.defaultStatus || 'todo'));
      const maxPos = colTasks.length > 0 ? Math.max(...colTasks.map(t => t.position)) + 1 : 0;
      const { data: inserted, error } = await supabase.from('tasks').insert({
        ...data,
        project_id: projectId,
        board_status: taskModal.defaultStatus || 'todo',
        position: maxPos,
        created_by: profile?.id,
      }).select().maybeSingle();
      if (!error && inserted) {
        await logAudit('insert', 'tasks', inserted.id, { title: data.title });
        loadData();
      }
    }
    setTaskModal({ open: false });
  };

  const handleSaveSprint = async (data: SprintFormData) => {
    if (!projectId) return;

    if (sprintModal.sprint?.id) {
      const prevSprint = sprintModal.sprint;
      const { error } = await supabase.from('sprints').update({
        ...data,
        updated_at: new Date().toISOString(),
      }).eq('id', sprintModal.sprint.id);

      if (!error) {
        // Se o valor mudou e é > 0, atualizar ou criar lançamento financeiro
        const prevValue = (prevSprint as { value?: number }).value ?? 0;
        if (data.value > 0 && data.value !== prevValue) {
          // Buscar client_id do projeto
          const { data: proj } = await supabase
            .from('projects')
            .select('client_id')
            .eq('id', projectId)
            .maybeSingle();

          // Verificar se já existe lançamento para esta sprint
          const { data: existing } = await supabase
            .from('financial_entries')
            .select('id')
            .eq('task_id', sprintModal.sprint.id)
            .eq('origin', 'sprint')
            .maybeSingle();

          if (existing) {
            await supabase.from('financial_entries').update({
              amount: data.value,
              description: `Sprint: ${data.name}`,
              updated_at: new Date().toISOString(),
            }).eq('id', existing.id);
          } else {
            await supabase.from('financial_entries').insert({
              project_id: projectId,
              client_id: proj?.client_id ?? null,
              task_id: sprintModal.sprint.id,
              type: 'income',
              origin: 'sprint',
              description: `Sprint: ${data.name}`,
              amount: data.value,
              is_paid: false,
              is_active: true,
              entry_date: data.start_date || new Date().toISOString().split('T')[0],
              due_date: data.end_date || null,
              created_by: profile?.id,
            });
          }
        }
        loadData();
      }
    } else {
      const { data: inserted, error } = await supabase.from('sprints').insert({
        ...data,
        project_id: projectId,
        created_by: profile?.id,
      }).select().maybeSingle();

      if (!error && inserted) {
        // Lançar no financeiro se tiver valor
        if (data.value > 0) {
          const { data: proj } = await supabase
            .from('projects')
            .select('client_id')
            .eq('id', projectId)
            .maybeSingle();

          await supabase.from('financial_entries').insert({
            project_id: projectId,
            client_id: proj?.client_id ?? null,
            task_id: inserted.id,
            type: 'income',
            origin: 'sprint',
            description: `Sprint: ${data.name}`,
            amount: data.value,
            is_paid: false,
            is_active: true,
            entry_date: data.start_date || new Date().toISOString().split('T')[0],
            due_date: data.end_date || null,
            created_by: profile?.id,
          });
        }
        loadData();
      }
    }
    setSprintModal({ open: false });
  };

  const activeSprint = sprints.find(s => s.status === 'active');
  const fmt = (d: string | null) => d ? new Date(d + 'T12:00:00').toLocaleDateString('pt-BR') : null;

  return (
    <AppLayout noPadding>
      <div className="flex flex-col h-full">
        {/* Top bar */}
        <div className="px-6 py-4 border-b border-slate-200 bg-white flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 shrink-0">
          <div className="flex items-center gap-3">
            <button
              onClick={() => navigate('/admin/scrum')}
              className="w-8 h-8 flex items-center justify-center rounded-lg text-slate-400 hover:text-slate-600 hover:bg-slate-100 transition-colors cursor-pointer"
            >
              <i className="ri-arrow-left-line text-base"></i>
            </button>
            <div>
              <div className="flex items-center gap-2">
                <h1 className="text-slate-800 text-lg font-bold">{project?.name || '...'}</h1>
                <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-cyan-100 text-cyan-700">Board</span>
              </div>
              <p className="text-slate-400 text-xs">{project?._client_name}</p>
            </div>
          </div>

          <div className="flex items-center gap-2 flex-wrap">
            <select
              value={selectedSprint}
              onChange={e => setSelectedSprint(e.target.value)}
              className="px-3 py-2 text-sm border border-slate-200 rounded-lg bg-white text-slate-700 focus:outline-none focus:ring-2 focus:ring-cyan-300 cursor-pointer"
            >
              <option value="all">Todos os itens</option>
              <option value="none">Sem sprint</option>
              {sprints.map(s => (
                <option key={s.id} value={s.id}>
                  {s.name} ({SPRINT_STATUS_CONFIG[s.status]?.label || s.status})
                </option>
              ))}
            </select>

            {isAdmin && (
              <button
                onClick={() => setSprintModal({ open: true })}
                className="flex items-center gap-1.5 px-3 py-2 border border-slate-200 text-slate-600 hover:bg-slate-50 rounded-lg text-sm font-medium transition-colors cursor-pointer whitespace-nowrap"
              >
                <i className="ri-time-line text-sm"></i>
                Nova Sprint
              </button>
            )}

            <button
              onClick={() => navigate(`/admin/scrum/${projectId}/backlog`)}
              className="flex items-center gap-1.5 px-3 py-2 border border-slate-200 text-slate-600 hover:bg-slate-50 rounded-lg text-sm font-medium transition-colors cursor-pointer whitespace-nowrap"
            >
              <i className="ri-list-check-2 text-sm"></i>
              Backlog
            </button>
          </div>
        </div>

        {/* Active sprint banner */}
        {activeSprint && (
          <div className="px-6 py-2.5 bg-emerald-50 border-b border-emerald-100 flex items-center justify-between shrink-0">
            <div className="flex items-center gap-2">
              <i className="ri-play-circle-fill text-emerald-500 text-sm"></i>
              <span className="text-emerald-700 text-sm font-medium">{activeSprint.name}</span>
              {activeSprint.goal && <span className="text-emerald-600 text-xs">— {activeSprint.goal}</span>}
            </div>
            <div className="flex items-center gap-3">
              {activeSprint.start_date && activeSprint.end_date && (
                <span className="text-emerald-600 text-xs">{fmt(activeSprint.start_date)} → {fmt(activeSprint.end_date)}</span>
              )}
              {isAdmin && (
                <button
                  onClick={() => setSprintModal({ open: true, sprint: activeSprint })}
                  className="text-emerald-600 hover:text-emerald-800 text-xs underline cursor-pointer"
                >
                  Editar
                </button>
              )}
            </div>
          </div>
        )}

        {/* Sprints list (non-active) */}
        {sprints.filter(s => s.status !== 'active').length > 0 && (
          <div className="px-6 py-2 border-b border-slate-100 bg-white flex items-center gap-2 overflow-x-auto shrink-0">
            <span className="text-slate-400 text-xs whitespace-nowrap">Sprints:</span>
            {sprints.filter(s => s.status !== 'active').map(s => {
              const cfg = SPRINT_STATUS_CONFIG[s.status];
              return (
                <div key={s.id} className="flex items-center gap-1.5 shrink-0">
                  <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${cfg.color}`}>{s.name}</span>
                  {isAdmin && (
                    <button
                      onClick={() => setSprintModal({ open: true, sprint: s })}
                      className="text-slate-400 hover:text-slate-600 cursor-pointer"
                      title="Editar sprint"
                    >
                      <i className="ri-edit-line text-xs"></i>
                    </button>
                  )}
                </div>
              );
            })}
          </div>
        )}

        {/* Kanban board */}
        {loading ? (
          <div className="flex items-center justify-center flex-1">
            <i className="ri-loader-4-line animate-spin text-cyan-500 text-2xl"></i>
          </div>
        ) : (
          <div className="flex-1 overflow-x-auto p-4">
            <div className="flex gap-3 h-full min-w-max">
              {COLUMNS.map(col => {
                const colTasks = getColumnTasks(col.key);
                const isDragTarget = dragOverCol === col.key;

                return (
                  <div
                    key={col.key}
                    className={`flex flex-col w-72 rounded-xl border-2 transition-all duration-150 ${
                      isDragTarget
                        ? 'border-cyan-400 bg-cyan-50/60 scale-[1.01]'
                        : 'border-slate-200 bg-slate-50/50'
                    }`}
                    onDragOver={e => handleDragOver(e, col.key)}
                    onDragLeave={handleDragLeave}
                    onDrop={e => handleDrop(e, col.key)}
                  >
                    {/* Column header */}
                    <div className={`flex items-center justify-between px-3 py-2.5 rounded-t-xl ${col.bg}`}>
                      <div className="flex items-center gap-2">
                        <div className={`w-2 h-2 rounded-full ${col.dot}`}></div>
                        <span className={`text-xs font-semibold ${col.color}`}>{col.label}</span>
                        <span className="w-5 h-5 flex items-center justify-center rounded-full bg-white text-slate-500 text-xs font-medium border border-slate-200">
                          {colTasks.length}
                        </span>
                      </div>
                      {isAdmin && col.key !== 'backlog' && (
                        <button
                          onClick={() => setTaskModal({ open: true, defaultStatus: col.key })}
                          className="w-6 h-6 flex items-center justify-center rounded-md text-slate-400 hover:text-slate-600 hover:bg-white transition-colors cursor-pointer"
                          title="Adicionar item"
                        >
                          <i className="ri-add-line text-sm"></i>
                        </button>
                      )}
                    </div>

                    {/* Drop zone indicator */}
                    {isDragTarget && (
                      <div className="mx-2 mt-2 h-1 rounded-full bg-cyan-400 animate-pulse"></div>
                    )}

                    {/* Tasks */}
                    <div className="flex-1 overflow-y-auto p-2 space-y-2 min-h-[200px]">
                      {colTasks.map(task => {
                        const prio = PRIORITY_CONFIG[task.priority] || PRIORITY_CONFIG.medium;
                        const sprintName = task.sprint_id
                          ? sprints.find(s => s.id === task.sprint_id)?.name
                          : null;
                        const isDragging = draggingId === task.id;

                        return (
                          <div
                            key={task.id}
                            draggable={isAdmin}
                            onDragStart={e => handleDragStart(e, task.id)}
                            onDragEnd={handleDragEnd}
                            className={`bg-white rounded-lg border p-3 transition-all select-none ${
                              isAdmin ? 'cursor-grab active:cursor-grabbing' : ''
                            } ${
                              isDragging
                                ? 'opacity-40 border-cyan-300 rotate-1'
                                : 'opacity-100 border-slate-200 hover:border-slate-300'
                            }`}
                          >
                            {/* Type + Priority */}
                            <div className="flex items-center justify-between mb-2">
                              <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-xs font-medium ${
                                task.type === 'bug' ? 'bg-red-50 text-red-600' : 'bg-violet-50 text-violet-600'
                              }`}>
                                <i className={task.type === 'bug' ? 'ri-bug-line' : 'ri-add-circle-line'}></i>
                                {task.type === 'bug' ? 'Bug' : 'Demanda'}
                              </span>
                              <span className={`inline-flex items-center gap-0.5 text-xs ${prio.color}`}>
                                <i className={prio.icon}></i>
                                {prio.label}
                              </span>
                            </div>

                            {/* Title */}
                            <p className="text-slate-800 text-sm font-medium leading-snug mb-2">{task.title}</p>

                            {/* Meta */}
                            <div className="flex items-center justify-between flex-wrap gap-1">
                              <div className="flex items-center gap-1.5">
                                {task.estimate_hours > 0 && (
                                  <span className="text-slate-400 text-xs flex items-center gap-0.5">
                                    <i className="ri-time-line"></i>{task.estimate_hours}h
                                  </span>
                                )}
                                {task.value > 0 && (
                                  <span className="text-slate-400 text-xs flex items-center gap-0.5">
                                    <i className="ri-money-dollar-circle-line"></i>
                                    {Number(task.value).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}
                                  </span>
                                )}
                              </div>
                              <div className="flex items-center gap-1">
                                {task.sprint_desired && (
                                  <i className="ri-star-fill text-amber-400 text-xs" title="Desejado para próxima sprint"></i>
                                )}
                                {task.paid && (
                                  <i className="ri-checkbox-circle-fill text-emerald-500 text-xs" title="Pago"></i>
                                )}
                              </div>
                            </div>

                            {sprintName && (
                              <div className="mt-2">
                                <span className="px-1.5 py-0.5 rounded text-xs bg-cyan-50 text-cyan-600">{sprintName}</span>
                              </div>
                            )}

                            {/* Actions */}
                            <div className="flex items-center justify-between mt-2 pt-2 border-t border-slate-50">
                              <span className="text-slate-400 text-xs truncate max-w-[120px]">
                                {task.requester_name || ''}
                              </span>
                              <div className="flex items-center gap-1">
                                <button
                                  onClick={e => {
                                    e.stopPropagation();
                                    setViewTask(task);
                                  }}
                                  title="Visualizar item"
                                  className="w-6 h-6 flex items-center justify-center rounded text-slate-300 hover:text-cyan-500 hover:bg-cyan-50 transition-colors cursor-pointer"
                                >
                                  <i className="ri-eye-line text-xs"></i>
                                </button>
                                {isAdmin && col.key !== 'done' && (
                                  <button
                                    onClick={e => {
                                      e.stopPropagation();
                                      handleAdvance(task);
                                    }}
                                    title={`Mover para ${COLUMNS[COLUMNS.findIndex(c => c.key === col.key) + 1]?.label}`}
                                    className="w-6 h-6 flex items-center justify-center rounded text-slate-400 hover:text-cyan-600 hover:bg-cyan-50 transition-colors cursor-pointer"
                                  >
                                    <i className="ri-arrow-right-line text-xs"></i>
                                  </button>
                                )}
                                <button
                                  onClick={e => {
                                    e.stopPropagation();
                                    setTaskModal({ open: true, task });
                                  }}
                                  title="Editar"
                                  className="w-6 h-6 flex items-center justify-center rounded text-slate-400 hover:text-slate-600 hover:bg-slate-100 transition-colors cursor-pointer"
                                >
                                  <i className="ri-edit-line text-xs"></i>
                                </button>
                              </div>
                            </div>
                          </div>
                        );
                      })}

                      {colTasks.length === 0 && (
                        <div className={`flex flex-col items-center justify-center py-8 rounded-lg border-2 border-dashed transition-colors ${
                          isDragTarget ? 'border-cyan-300 text-cyan-400' : 'border-slate-200 text-slate-300'
                        }`}>
                          <i className="ri-inbox-line text-2xl mb-1"></i>
                          <p className="text-xs">{isDragTarget ? 'Soltar aqui' : 'Vazio'}</p>
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>

      {taskModal.open && (
        <TaskModal
          task={taskModal.task ? {
            id: taskModal.task.id,
            title: taskModal.task.title,
            description: taskModal.task.description || '',
            type: taskModal.task.type,
            priority: taskModal.task.priority,
            requester_name: taskModal.task.requester_name || '',
            observations: taskModal.task.observations || '',
            estimate_hours: taskModal.task.estimate_hours,
            estimate_days: taskModal.task.estimate_days,
            value: taskModal.task.value,
            paid: taskModal.task.paid,
            sprint_desired: taskModal.task.sprint_desired,
            board_status: taskModal.task.board_status,
            sprint_id: taskModal.task.sprint_id,
          } : undefined}
          sprints={sprints}
          isAdmin={isAdmin}
          onClose={() => setTaskModal({ open: false })}
          onSave={handleSaveTask}
        />
      )}

      {sprintModal.open && (
        <SprintModal
          sprint={sprintModal.sprint}
          onClose={() => setSprintModal({ open: false })}
          onSave={handleSaveSprint}
        />
      )}

      {/* Visualizar item com chat integrado */}
      {viewTask && (
        <TaskViewModal
          task={{
            id: viewTask.id,
            project_id: viewTask.project_id,
            title: viewTask.title,
            description: viewTask.description,
            type: viewTask.type,
            priority: viewTask.priority,
            board_status: viewTask.board_status,
            requester_name: viewTask.requester_name,
            observations: viewTask.observations,
            estimate_hours: viewTask.estimate_hours,
            estimate_days: viewTask.estimate_days,
            value: viewTask.value,
            paid: viewTask.paid,
            sprint_desired: viewTask.sprint_desired,
            sprint_id: viewTask.sprint_id,
            position: viewTask.position,
            client_priority: 0,
            created_by: viewTask.created_by,
            created_at: viewTask.created_at,
          }}
          userId={profile?.id || ''}
          userName={profile?.full_name || 'Admin'}
          canEdit={isAdmin}
          onEdit={() => { setViewTask(null); setTaskModal({ open: true, task: viewTask }); }}
          onClose={() => setViewTask(null)}
        />
      )}
    </AppLayout>
  );
}